import type { Group, MeshStandardMaterial } from 'three';

import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import { createIslandGroupCfn } from './create-island.cfn';
import {
	createSurroundPanelCfn,
	createTrimPanelCfn,
	createCountertopCfn
} from './create-geometry.cfn';
import { loadAppliancesForIslandCfn } from './load-appliances.cfn';
import { regenerateCutoutsForIslandsCfn } from './generate-cutouts.cfn';
import { normalizeSurroundKey, normalizeCountertopKey } from '../helpers';

const syncMaterialFromTextureManager = (self: KitchenSceneBuilder, key: string | null | undefined): void => {
	if (!key) return;
	if (self.materialsAccess.has(key)) return;
	const textureManager = self.configAccess.textureManager;
	if (!textureManager) return;
	const mat = textureManager.getMaterial(key);
	if (mat) {
		self.materialsAccess.set(key, mat as MeshStandardMaterial);
		self.geometryBuilderAccess.setMaterials(self.materialsAccess);
	}
};

/**
 * Build a single island group from the current kitchen data.
 * Reuses existing per-island build logic without touching other islands.
 * Textures are expected to already be cached from the initial build.
 *
 * Returns the new island Group ready to be inserted into the scene.
 */
export const buildSingleIslandCfn = async (
	self: KitchenSceneBuilder,
	{ islandId, input }: { islandId: string; input: KitchenBlueprintInput }
): Promise<Group | null> => {
	// Re-init kitchen data from current Kitchen state
	if (input.kitchen) {
		self.kitchenDataManagerAccess.init(input.kitchen);
	}

	const islands = self.kitchenDataManagerAccess.getIslands();
	const islandIndex = islands.findIndex((isl: any) => isl.id === islandId);
	if (islandIndex === -1) {
		console.warn(`[buildSingleIsland] Island not found: ${islandId}`);
		return null;
	}

	const island = islands[islandIndex];

	// Step 1: Create island group container
	const islandGroup = createIslandGroupCfn(self, { island, index: islandIndex });

	// Step 2: Create geometry (panels, trims, countertop) for this island.
	// All geometry operations run in parallel to minimize total rebuild time.
	// CSG operations (slat textures) are synchronous per-panel, but running them
	// via Promise.all eliminates the yield overhead between panels.
	const surroundKey = normalizeSurroundKey(self.kitchenDataManagerAccess.getKitchenMaterial()?.surround?.key);
	// Resolve from the per-island countertop material so a per-island "No Countertop"/override
	// is honored (not the kitchen default).
	const countertopKey = normalizeCountertopKey(island.counter?.material?.key);

	// On-demand loads (e.g., slat <-> non-slat switch) populate textureManager but
	// not the builder's local materials map — sync any that arrived since the last build.
	syncMaterialFromTextureManager(self, surroundKey);
	syncMaterialFromTextureManager(self, surroundKey ? `${surroundKey}-corner` : null);
	syncMaterialFromTextureManager(self, countertopKey);

	const geometryPromises: Array<Promise<void>> = [];

	if (island.panels) {
		for (let panelIndex = 0; panelIndex < island.panels.length; panelIndex++) {
			const panel = island.panels[panelIndex];
			geometryPromises.push(createSurroundPanelCfn(self, { panel, islandIndex, panelIndex, islandGroup, surroundKey }));
		}
	}

	if (island.panelTrims) {
		for (let trimIndex = 0; trimIndex < island.panelTrims.length; trimIndex++) {
			const trim = island.panelTrims[trimIndex];
			geometryPromises.push(createTrimPanelCfn(self, { trim, islandIndex, trimIndex, islandGroup, surroundKey }));
		}
	}

	if (island.counter) {
		geometryPromises.push(createCountertopCfn(self, { island, islandIndex, islandGroup, countertopKey }));
	}

	await Promise.all(geometryPromises);

	// Step 3: Load appliance models for this island
	if (input.controller?.options?.showAppliances !== false) {
		await loadAppliancesForIslandCfn(self, { input, islandIndex, targetGroup: islandGroup });
	}

	// Step 4: Generate cutouts for this island
	// Pass targetIslandIndex so only this island's cutouts are generated,
	// remapped to index 0 to match the single-element islandGroups array
	if (input.controller?.options?.showCutouts !== false) {
		await regenerateCutoutsForIslandsCfn(self, { islandGroups: [islandGroup], targetIslandIndex: islandIndex });
	}

	return islandGroup;
};
